import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { requiredFieldClass } from "../lib/requiredFieldClass";
import "./Form.css"; 

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getCalendarMatrix = (year, month) => {
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysCount = getDaysInMonth(year, month);
  const matrix = [];
  let week = new Array(7).fill(null);
  let day = 1;
  for (let i = firstDayIndex; i < 7; i++) {
    week[i] = new Date(year, month, day++);
  }
  matrix.push(week);

  while (day <= daysCount) {
    week = Array(7).fill(null);
    for (let i = 0; i < 7; i++) {
      if (day <= daysCount) {
        week[i] = new Date(year, month, day++);
      }
    }
    matrix.push(week);
  }
  return matrix;
};

const SeiriLeaveForm = () => {
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  
  const [reason, setReason] = useState("生理休暇の取得");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  // スケジュール登録画面から渡された生休日付を読み込む
  useEffect(() => {
    try {
      const calendarDates = localStorage.getItem("calendar_seiri_leave_dates");
      if (calendarDates) {
        const parsed = JSON.parse(calendarDates);
        localStorage.removeItem("calendar_seiri_leave_dates");
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedDates(parsed.map((d) => String(d).slice(0, 10)).sort());
          setShowCalendar(true);
        }
      }
    } catch (_) {}
  }, []);

  // 申請期間の日数を計算するロジック
  const totalDays = useMemo(() => {
    return selectedDates.length;
  }, [selectedDates]);
  
  const currentYear = currentCalendarDate.getFullYear();
  const currentMonth = currentCalendarDate.getMonth();
  const calendarMatrix = useMemo(() => getCalendarMatrix(currentYear, currentMonth), [currentYear, currentMonth]);
   
  const handlePrevMonth = () => {
    setCurrentCalendarDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentCalendarDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const ymd = formatYmd(date);
    if (selectedDates.includes(ymd)) {
      setSelectedDates(selectedDates.filter((d) => d !== ymd));
    } else {
      setSelectedDates([...selectedDates, ymd].sort());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDates.length === 0) {
      setMessage("生理休暇の取得日を選択してください。");
      return;
    }

    if (!window.confirm(`生理休暇申請を送信します。よろしいですか？\n※この申請は自動的に承認され、有給休暇は消費されません。`)) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // 申請期間内のすべての日付をリストに追加する
      
      const payload = {
        category: "SERI", 
        title: `生理休暇申請（${selectedDates.join(", ")}）`,
        content: reason,
        formData: {
          selectedDates: selectedDates,
          description: reason,
          totalDays: totalDays,
        }
      };

      const res = await api.post("/forms/submit_seiri_leave/", payload); 
      
      if (res.status === 201 || res.data?.id) {
        alert(`生理休暇を申請しました。合計 ${totalDays} 日間が自動承認されました。`);
        navigate("/applications");
      }
    } catch (err) {
      console.error(err);
      setMessage("申請の送信に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h3 className="form-title">生理休暇申請</h3>
      <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "20px", textAlign: "center" }}>
        ※生理休暇は有給休暇の残日数は消費されません。
      </p>

      {message && <div className="form-alert">{message}</div>}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          {/*<label className="form-label">休暇取得日 <span style={{ color: "red" }}>*</span></label>*/}
          <div>
            <button
              type="button"
              className="paid-leave-day-toggle"
            
              /*
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "10px",
                backgroundColor: showCalendar ? "#e2e8f0" : "#1e7bd4",
                color: showCalendar ? "#334155" : "#fff",
                border: "1px solid #cbd5e1"
              }}
              */
              onClick={() => setShowCalendar(!showCalendar)}
              disabled={loading}
            >
              <span className="form-label" style={{ marginBottom: 0 }}>生休</span>
              {/*{showCalendar ? "カレンダー" : "生理休暇取得"}*/}
              <span className="paid-leave-day-toggle-icon" aria-hidden="true">
                {showCalendar ? "▲" : "▼"}
              </span>
            </button>
          </div>

          
          {showCalendar && (
            <div className="paid-leave-calendar-wrapper" style={{ marginTop: "10px" }}>
              <div className="paid-leave-calendar-header">
                <button type="button" className="paid-leave-calendar-nav-btn" onClick={handlePrevMonth} disabled={loading}>
                  &lt;
                </button>
                <span className="paid-leave-calendar-current-month">
                  {currentYear}年 {currentMonth + 1}月
                </span>
                <button type="button" className="paid-leave-calendar-nav-btn" onClick={handleNextMonth} disabled={loading}>
                  &gt;
                </button>
              </div>

              <div className="paid-leave-calendar-weekdays">
                {WEEK_LABELS.map((label, idx) => {
                  let cls = "paid-leave-calendar-weekday";
                  if (idx === 0) cls += " is-sun";
                  if (idx === 6) cls += " is-sat";
                  return (
                    <div key={idx} className={cls}>
                      {label}
                    </div>
                  );
                })}
              </div>

              <div className="paid-leave-calendar-grid">
                {calendarMatrix.map((week, wIdx) =>
                  week.map((date, dIdx) => {
                    if (!date) {
                      return <div key={`empty-${wIdx}-${dIdx}`} className="paid-leave-calendar-cell-empty" />;
                    }
                    const ymd = formatYmd(date);
                    const isSelected = selectedDates.includes(ymd);

                    let cellClass = "paid-leave-calendar-cell";
                    if (isSelected) cellClass += " is-selected";
                    if (dIdx === 0) cellClass += " is-sun";
                    if (dIdx === 6) cellClass += " is-sat";

                    return (
                      <button
                        key={ymd}
                        type="button"
                        className={cellClass}
                        onClick={() => handleDateClick(date)}
                        disabled={loading}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
    
        </div>
        
        {totalDays > 0 && (
          <div style={{ margin: "15px 0", padding: "10px", background: "#e8f5e9", borderRadius: "6px", textAlign: "center", color: "#2e7d32", fontWeight: "bold" }}>
            申請合計日数: {totalDays} 日間
          </div>
        )}
        
        <div className="button-group">
          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? "送信中..." : "申請"}
          </button>
          <button type="button" className="btn-back" onClick={() => navigate("/applications")} disabled={loading}>
            戻る
          </button>
        </div>
      </form>
    </div>
  );
};

export default SeiriLeaveForm;